import {AxiosError} from 'axios';
import {useEffect, useCallback, useState, useMemo, useRef} from 'react';

import {clientActionsApi} from 'api';
import {envConfig} from 'config';
import {CLIENT_VALIDATION_ERROR_TYPE} from 'constants/client';
import {STATUS_CODES} from 'constants/http';
import {PARTICIPANT_NOTIFICATION} from 'constants/session';
import {SocketDisconnectReason, SocketEvent, SocketManagerEvent} from 'enums';
import {useClientSession, useClientSocket, useSocketEventCallback} from 'hooks';
import {ClientDevice, ClientSession, LiveHelpMode, SessionActivityName, SessionStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeToken, LanguageService} from 'services';
import {
    ApiError, ClientSessionParams, ClientStartSessionConnectParams, ClientStartSessionParams, NotifyClientResult,
    PreSessionFlowState, PreSessionStepStatus, PreSessionFlowMode,
} from 'types';
import {logger, sessionUtils} from 'utils';

import {ClientNotifier} from './ClientNotifier';
import {ClientSessionActions} from './SessionActions';
import {mockStartSessionParams} from './constants';
import {ClientAppearanceFeatures, ClientSessionResponsePayload, ClientView, SessionActionsContainerContextProps} from './types';

const {isDevelopmentMode} = envConfig;
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;

const invalidAssignmentErrorTypes = [
    CLIENT_VALIDATION_ERROR_TYPE.INVALID_ACCOUNT,
    CLIENT_VALIDATION_ERROR_TYPE.INVALID_TOKEN,
    CLIENT_VALIDATION_ERROR_TYPE.INVALID_GROUP,
    CLIENT_VALIDATION_ERROR_TYPE.INVALID_SUBGROUP,
];

type Props = {
    device: ClientDevice;
    clientNotifier: ClientNotifier;
    conversationOnly?: boolean;
    features?: ClientAppearanceFeatures;
    children: React.ReactElement;
}

export const ClientSessionStartupContainer: React.FC<Props> = ({
    device, clientNotifier, conversationOnly, features, children
}: Props) => {
    const [responsePayload, setResponsePayload] = useState<ClientSessionResponsePayload>();
    const [clientSessionParams, setClientSessionParams] = useState<ClientSessionParams>();
    const [localSessionEnded, setLocalSessionEnded] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [startSessionInProgress, setStartSessionInProgress] = useState(false);
    const [reassigned, setReassigned] = useState(false);
    const [languageInitialized, setLanguageInitialized] = useState(false);
    const [reassignInProgress, setReassignInProgress] = useState(false);
    const [errorMessageTKey, setErrorMessageTKey] = useState<string>();
    const [waitingStartSessionFromChildren, setWaitingStartSessionFromChildren] = useState(false);
    const [notifiedAppReady, setNotifiedAppReady] = useState(false);
    const reassignedRef = useRef(false); // used for disconnect event checking

    const {
        sessionId, session, sessionParams, sessionVersion, inWidget, sessionStatus, activitiesCount, textChatActivity,
        agentsUnavailable, isAfterHours, token, latestToken, isEnded, language, isAppointmentRequest, sipActivity, avatarActivity,
        asyncRequestToHandle
    } = useClientSession({payload: responsePayload});

    const canRenderChildren = useMemo(() => !!session || !!errorMessageTKey || waitingStartSessionFromChildren,
        [errorMessageTKey, session, waitingStartSessionFromChildren]);

    const socketEnabled = useMemo(() => !!session && !agentsUnavailable && !isEnded && !localSessionEnded && canRenderChildren,
        [session, agentsUnavailable, isEnded, localSessionEnded, canRenderChildren]);

    const {connected, socket} = useClientSocket(token, {enabled: socketEnabled});
    useEffect(() => logger.debug(`Socket connection state changed. Connected: ${connected}`), [connected]);

    const isFullScreenTextChat = useMemo(() => inWidget &&
        [SessionStatus.PreCall, SessionStatus.Chatbot, SessionStatus.Calling, SessionStatus.InSession].includes(sessionStatus) &&
        activitiesCount === 1 && textChatActivity, [activitiesCount, inWidget, sessionStatus, textChatActivity]);

    const isSipCall = useMemo(() => !localSessionEnded && sipActivity, [localSessionEnded, sipActivity]);
    const isAvatarCall = useMemo(() => !localSessionEnded && avatarActivity, [localSessionEnded, avatarActivity]);

    // Pre-session-flow takes precedence over TextChat/Calling/Avatar views while the snapshot
    // has a non-terminal current step OR is awaiting client confirmation to escalate. Snapshot
    // lives on whichever default activity is active (TextChat or Avatar) — see backend
    // pre-session-flow.service `activateFlow`.
    const isPreSessionFlowActive = useMemo(() => {
        if (localSessionEnded) {
            return false;
        }
        const data = (textChatActivity?.data || avatarActivity?.data) as
            {preSessionFlow?: PreSessionFlowState} | undefined;
        const snapshot = data?.preSessionFlow;
        if (!snapshot) {
            return false;
        }
        if (snapshot.awaitingConfirmation) {
            return true;
        }
        const currentStep = snapshot.flowSnapshot?.steps?.[snapshot.currentStepIndex];
        if (!currentStep) {
            return false;
        }
        const state = snapshot.steps?.find((s) => s.stepId === currentStep.id);
        // Pending: snapshot just initialized server-side, `triggerStep` socket event may not
        // have arrived yet (race on initial client-connect payload). Keep view on pre-session
        // so client doesn't fall through to Loading while waiting for activation.
        return state?.status === PreSessionStepStatus.InProgress ||
            state?.status === PreSessionStepStatus.Pending;
    }, [textChatActivity, avatarActivity, localSessionEnded]);

    // Under the chatbot the whole flow happens in the conversation: form steps are answered by talking,
    // and the controls for the rest (files, screenshots, vendor frames) are rendered in the chat itself
    // by PreSessionStepCard. Taking the screen for the wizard would cut the client off from the
    // assistant mid-flow, exactly when they are most likely to have a question.
    const isPreSessionFlowInChat = useMemo(() => {
        const data = (textChatActivity?.data || avatarActivity?.data) as
            {preSessionFlow?: PreSessionFlowState} | undefined;
        return !!data?.preSessionFlow?.flowSnapshot?.modes?.chatbot
            && data.preSessionFlow.activeMode !== PreSessionFlowMode.Form;
    }, [textChatActivity, avatarActivity]);

    // A conversational flow that has run out of steps carries on as a chat with the chatbot; without this the
    // client, outside the widget, would fall through to the loading view.
    const isPreSessionFlowCompletedInChat = useMemo(() => {
        if (![SessionStatus.PreCall, SessionStatus.Chatbot].includes(sessionStatus)) {
            return false;
        }
        const data = (textChatActivity?.data || avatarActivity?.data) as
            {preSessionFlow?: PreSessionFlowState} | undefined;
        return !!data?.preSessionFlow?.completedInChat;
    }, [textChatActivity, avatarActivity, sessionStatus]);

    const currentView: ClientView = useMemo(() => {
        if (!conversationOnly) {
            // analyze state values
            const expectCallingState = clientSessionParams?.liveHelpMode !== LiveHelpMode.Widget ||
                clientSessionParams?.requestedActivity !== SessionActivityName.TextChat;
            if (localSessionEnded) {
                if (isAfterHours) {
                    return ClientView.AfterHours;
                } else if (agentsUnavailable) {
                    return ClientView.AgentsUnavailable;
                } else if (isAppointmentRequest) {
                    return ClientView.RequestAppointment;
                } else {
                    return ClientView.SessionEnded;
                }
            } else if (isSipCall) {
                return ClientView.SIP;
            } else if (asyncRequestToHandle && !isPreSessionFlowInChat) {
                // Pre-session-flow's AsyncRequest steps (geolocation, Suma SDK, etc.) need the
                // browser permission prompt UI which lives in FullScreenAsyncRequest. Let it take
                // over while a request is InProgress; once resolved, snapshot advances and
                // PreSessionFlowView (or Calling) takes over again.
                return ClientView.FullScreenAsyncRequest;
            } else if (isPreSessionFlowActive) {
                return isPreSessionFlowInChat ? ClientView.FullScreenTextChat : ClientView.PreSessionFlow;
            } else if (isPreSessionFlowCompletedInChat) {
                return ClientView.FullScreenTextChat;
            } else if (startSessionInProgress && expectCallingState) {
                return ClientView.Calling;
            } else if (isFullScreenTextChat) {
                return ClientView.FullScreenTextChat;
            }

            if (isAvatarCall) {
                return ClientView.Avatar;
            }

            // analyze session status
            if (sessionStatus === SessionStatus.Calling) {
                return ClientView.Calling;
            } else if (sessionStatus === SessionStatus.New) {
                return ClientView.WaitingForAgent;
            } else if (sessionStatus === SessionStatus.OnHold) {
                return ClientView.OnHold;
            } else if (sessionStatus === SessionStatus.InSession) {
                return ClientView.InSession;
            }
            return ClientView.Loading;
        }

        if (sessionStatus === SessionStatus.InSession) {
            return ClientView.InSession;
        }
        return null;
    }, [agentsUnavailable, clientSessionParams, conversationOnly, isFullScreenTextChat, localSessionEnded,
        sessionStatus, isAfterHours, startSessionInProgress, isSipCall, isAppointmentRequest, isAvatarCall,
        asyncRequestToHandle, isPreSessionFlowActive, isPreSessionFlowInChat, isPreSessionFlowCompletedInChat]);

    // console.log({isFullScreenTextChat, sessionStatus, currentView, activitiesCount, textChatActivity: !!textChatActivity});

    const handleSessionPayload = useCallback((payload: Partial<ClientSessionResponsePayload>) => {
        const {session: newSession} = payload;
        if (newSession.version < sessionVersion) {
            logger.warn('An invalid version sequence occurred. Update ignored');
            return;
        }
        if (newSession.version === sessionVersion) {
            setResponsePayload((prev) => ({...prev, ...payload, session: {...(session && session), ...newSession}}));
            return;
        }
        setResponsePayload((prev) => ({...(prev && prev), ...payload}));
    }, [sessionVersion, session]);

    useSocketEventCallback(socket, SocketEvent.ActiveSessionChanged, useCallback((_session: ClientSession) => {
        logger.debug(`Received socket event: ${SocketEvent.ActiveSessionChanged}. Version: ${_session?.version}`, _session);
        handleSessionPayload({session: _session});
    }, [handleSessionPayload]));

    const appReadyToStartSessionFromChildren = useCallback(() => {
        InviteeToken.remove();
        setWaitingStartSessionFromChildren(true);
    }, []);

    const getIsInviteeTokenValid = useCallback(async (): Promise<boolean> => {
        try {
            const {isValid} = await clientActionsApi.validateToken();
            return isValid;
        } catch {
            return false;
        }
    }, []);

    const getClientState = useCallback((params: any = {}): Promise<ClientSessionResponsePayload> =>
        clientActionsApi.getClientState({...params}), []);

    const getErrorMessageTKey = useCallback((errorType: string): string => {
        if (invalidAssignmentErrorTypes.includes(errorType)) {
            return T_KEYS.MESSAGES.INVALID_ASSIGNMENT;
        } else if (errorType === CLIENT_VALIDATION_ERROR_TYPE.INVALID_OBJECT_STRUCTURE) {
            return T_KEYS.MESSAGES.INVALID_CLIENT_DATA;
        } else if (errorType === CLIENT_VALIDATION_ERROR_TYPE.INVALID_CONNECT_TOKEN) {
            return T_KEYS.MESSAGES.INVALID_CONNECT_TOKEN;
        }
        return T_KEYS.MESSAGES.UNEXPECTED_ERROR;
    }, []);

    // TODO: fix backend response to match ApiError type
    const handleStartSessionErrorResponse = useCallback((error: AxiosError<any>) => {
        let errorType = null;
        if (error?.response?.status === STATUS_CODES.UNPROCESSABLE_ENTITY) {
            errorType = error?.response?.data?.errorType || null;
            clientNotifier.validationError(errorType);
        }
        setErrorMessageTKey(getErrorMessageTKey(errorType));
        setLocalSessionEnded(true);
    }, [clientNotifier, getErrorMessageTKey]);

    const proceedWithStartedSession = useCallback(() => {
        setWaitingStartSessionFromChildren(false);
        setNotifiedAppReady(true);
    }, []);

    const tryProceedWithStartedSession = useCallback(async () => {
        try {
            const data = await getClientState({settings: true, timestamp: 0});
            handleSessionPayload({token: InviteeToken.get(), ...data});
            proceedWithStartedSession();
        } catch (e) {
            const error = e as ApiError;
            const status = error?.response?.status;
            if ([STATUS_CODES.UNAUTHORIZED, STATUS_CODES.NOT_FOUND].includes(status)) {
                appReadyToStartSessionFromChildren();
            } else {
                handleStartSessionErrorResponse(error);
            }
        }
    }, [appReadyToStartSessionFromChildren, getClientState, handleSessionPayload, handleStartSessionErrorResponse,
        proceedWithStartedSession]);

    const notifySession = useCallback(async (name: string, data?: any, clientToken?: string) => {
        const event = {
            name,
            ...(data && {data})
        };
        try {
            return await clientActionsApi.notifyClientSession(sessionParams, event, clientToken);
        } catch (error) {
            logger.error('Notification send failed', {error, event});
            return Promise.resolve();
        }
    }, [sessionParams]);

    const handleNotificationResult = useCallback(async (result: NotifyClientResult, clientToken?: string) => {
        if (InviteeToken.has() || clientToken) {
            const data = {
                name: result.name,
                notified: result.success,
                ...(result.description && {description: result.description}),
                ...(result.error && {error: result.error}),
            };
            await notifySession(PARTICIPANT_NOTIFICATION.CLIENT_EVENT, data, clientToken);
        }
    }, [notifySession]);

    const handleStartSessionResponse = useCallback((_responsePayload: ClientSessionResponsePayload) => {
        const {token: _token, assignmentState: _assignmentState, disabled} = _responsePayload;
        if (disabled) {
            setErrorMessageTKey(T_KEYS.MESSAGES.CLIENT_CONNECT_DISABLED);
            setLocalSessionEnded(true);
            return;
        }
        const _isEnded = _responsePayload.session.status === SessionStatus.SessionEnded;

        if (token !== _token) {
            InviteeToken.set(_token);
        }

        if (_assignmentState.agentsAvailable) {
            handleNotificationResult(clientNotifier.agentsAvailable());
        } else {
            handleNotificationResult(clientNotifier.agentsUnavailable());
        }

        setLocalSessionEnded(_isEnded);
        handleSessionPayload(_responsePayload);
    }, [clientNotifier, token, handleSessionPayload, handleNotificationResult]);

    const handleStartSessionPromise = useCallback((promise: Promise<any>) => {
        setStartSessionInProgress(true);
        return promise
            .then(handleStartSessionResponse)
            .catch(handleStartSessionErrorResponse)
            .finally(() => setStartSessionInProgress(false));
    }, [handleStartSessionErrorResponse, handleStartSessionResponse]);

    const startSessionWithParams = useCallback((params: ClientSessionParams) => {
        if (sessionId || !waitingStartSessionFromChildren) {
            logger.warn('Unexpected start session request');
            return;
        }

        setWaitingStartSessionFromChildren(false);

        const clientStartSessionParams: ClientStartSessionParams = {
            ...(isDevelopmentMode && mockStartSessionParams),
            ...(params && params),
            device,
            ...(sessionUtils.isClientDeviceSDK(device) && {
                appearance: {
                    webview: true,
                    features
                }
            })
        };

        setClientSessionParams(params);
        handleStartSessionPromise(clientActionsApi.startSession(clientStartSessionParams));
    }, [sessionId, device, waitingStartSessionFromChildren, features, handleStartSessionPromise]);

    const startSessionConnect = useCallback((_token: string) => {
        if (sessionId || !waitingStartSessionFromChildren) {
            logger.warn('Unexpected start session connect request');
            return;
        }

        setWaitingStartSessionFromChildren(false);

        const clientStartSessionParams: ClientStartSessionConnectParams = {
            token: _token,
            device,
        };

        handleStartSessionPromise(clientActionsApi.startSessionConnect(clientStartSessionParams));
    }, [sessionId, device, waitingStartSessionFromChildren, handleStartSessionPromise]);

    const toggleReassigned = useCallback((value: boolean) => {
        setReassigned(value);
        reassignedRef.current = value;
    }, []);

    const handleStartReassignedSession = useCallback(() => {
        setResponsePayload(null);
        toggleReassigned(true);
    }, [toggleReassigned]);

    const initialize = useCallback(async () => {
        if (InviteeToken.has() && await getIsInviteeTokenValid()) {
            tryProceedWithStartedSession();
        } else {
            appReadyToStartSessionFromChildren();
        }
    }, [appReadyToStartSessionFromChildren, tryProceedWithStartedSession, getIsInviteeTokenValid]);

    useSocketEventCallback(socket, SocketEvent.Unauthorized, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.Unauthorized}`);
        setErrorMessageTKey(getErrorMessageTKey(CLIENT_VALIDATION_ERROR_TYPE.INVALID_CONNECT_TOKEN));
    }, [getErrorMessageTKey]));

    useSocketEventCallback(socket, SocketManagerEvent.ReconnectAttempt, useCallback((attempt: number) => {
        logger.debug(`Received socket event: ${SocketManagerEvent.ReconnectAttempt}`, attempt);
    }, []));

    useSocketEventCallback(socket, SocketEvent.AliveCheck, useCallback((ack: any) => {
        logger.debug(`Received socket event: ${SocketEvent.AliveCheck}`);
        ack();
    }, []));

    useSocketEventCallback(socket, SocketEvent.Disconnect, useCallback((reason: SocketDisconnectReason) => {
        const willReconnect = !(socket.io as any).skipReconnect; // remove when typings fixed in socket.io
        logger.debug(`Received socket event: ${SocketEvent.Disconnect} with reason "${reason}". Will reconnect: `, willReconnect);
        if (!reassignedRef.current && (!reason || reason === SocketDisconnectReason.ServerDisconnect || !willReconnect)) {
            setLocalSessionEnded(true);
        }
    }, [socket]));

    const contextProps: SessionActionsContainerContextProps = useMemo(() => ({
        isDevelopmentMode, currentView, conversationOnly, errorMessageTKey, waitingStartSessionFromChildren, device, features,
        startSessionConnect, startSessionWithParams,
    }), [currentView, conversationOnly, errorMessageTKey, waitingStartSessionFromChildren, device, features,
        startSessionConnect, startSessionWithParams]);

    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
            initialize();
        }
    }, [initialize, initialized]);

    useEffect(() => {
        if (reassigned && !reassignInProgress) {
            setReassignInProgress(true);
            const promise = clientActionsApi.startReassignedSession(sessionParams, latestToken);
            handleStartSessionPromise(promise);
            promise.finally(() => {
                toggleReassigned(false);
                reassignedRef.current = false;
                setReassignInProgress(false);
            });
        }
    }, [reassigned, sessionParams, latestToken, reassignInProgress, toggleReassigned, handleStartSessionPromise]);

    useEffect(() => {
        if (waitingStartSessionFromChildren && !notifiedAppReady) {
            clientNotifier.appReady();
            setNotifiedAppReady(true);
        }
    }, [waitingStartSessionFromChildren, notifiedAppReady, clientNotifier]);

    useEffect(() => {
        if (responsePayload && !languageInitialized) {
            setLanguageInitialized(true);
            if (!LanguageService.isStorageLanguageValid() && LanguageService.isValidLanguage(language)) {
                LanguageService.setLanguage(language);
            }
        }
    }, [languageInitialized, responsePayload, language]);

    if (!canRenderChildren) {
        return null;
    }

    return (
        <ClientSessionActions
            socket={socket}
            payload={responsePayload}
            contextProps={contextProps}
            device={device}
            clientNotifier={clientNotifier}
            localSessionEnded={localSessionEnded}
            setLocalSessionEnded={setLocalSessionEnded}
            startReassignedSession={handleStartReassignedSession}
            handleNotificationResult={handleNotificationResult}
        >
            {children}
        </ClientSessionActions>
    );
};